import $ from 'jquery';
import MpModal from './modal.js';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

/**
 * @typedef {(this: MpDialog, dialog: MpDialog, element: HTMLElement, id: number) => void} MpDialogCallback
 */

/**
 * @typedef {{
 * type?: string;
 * text?: string;
 * attributes?: any;
 * callback?: MpDialogCallback;
 * }} MpDialogButtonConf
 */

/**
 * @typedef {{
 * titleIcon?: { type?: string; format?: string; size?: string; right?: string; };
 * buttons?: MpDialogButtonConf[],
 * } & import('./modal').MpModalOptions} MpModalOptions
 */
export const Default = {
	backdrop: 'static',
	keyboard: true,
	// TODO: fix focus logic
	focus: true,
	centered: true,
	scrollable: true,
	dismissible: true,
	disposeOnHide: true,
	overlappable: true,
	size: 'md',
	fullscreen: false, // no fullscreen necessary for dialogs
};

export const DefaultType = {
	buttons: 'array',
};

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

/**
 * Modal component
 * @class
 */
// @ts-ignore
export default class MpDialog extends MpModal {

	// ==============
	// Props
	// ==============

	// =======================================================================================

	// ==============
	// Static Getters
	// ==============

	static get DefaultType() {
		return { ...MpModal.DefaultType, DefaultType };
	}

	static get Default() {
		return { ...MpModal.Default, ...Default };
	}

	// ==============
	// Static Methods
	// ==============

	/**
	 * @param {MpDialogButtonConf[]} buttons
	 */
	static buildButtons(buttons = []) {

		const footerConfig = mpro.config.ui.dialog.footer;
		const $footer = $(document.createElement(footerConfig.type || 'div'));
		const callbackIds = [];

		// Add attributes
		if (footerConfig.attributes) {
			$footer.attr(footerConfig.attributes);
		}

		for (const buttonData of buttons) {

			// Create markup
			const $button = $(document.createElement(buttonData.type || 'button'));

			const attr = buttonData.attributes || {};
			attr['data-dialog-cb-id'] = callbackIds.length;

			// Store the callback for later
			callbackIds.push(buttonData.callback);

			// Add attributes
			if (buttonData.attributes) {
				$button.attr(buttonData.attributes);
			}

			// Add text
			$button.text(buttonData.text);

			// Append in the footer
			$footer.append($button);
		}

		return {
			$footer,
			callbackIds,
		};
	}

	/**
	 * @param {MpModalOptions} options
	 */
	static ask(options = {}) {

		const dialogConfig = mpro.config.ui.dialog;
		const { $footer, callbackIds } = this.buildButtons(options.buttons);

		// Add title icon
		if (options?.titleIcon?.type) {
			const title = options.title || '';
			const { type: iconType, ...iconOptions } = options.titleIcon;
			options.title = `${mpro.icon(iconType, iconOptions)} ${title}`;
		}

		const dialog = this.show({
			wrapperClass: dialogConfig.wrapper.class,
			bodyClass: dialogConfig.body.class,
			...options,
			footer: $footer.length > 0 ? $footer.wrapAll('<div/>').parent().html() : null,
		});

		// Setup callbacks on buttons
		const $dialogFooter = dialog.getFooter$();
		for (const [cbId, callback] of callbackIds.entries()) {
			if (!callback) {
				continue;
			}
			const $button = $dialogFooter.find(`[data-dialog-cb-id="${cbId}"]`);
			$button.on('click.dialog-action', function(e) {
				e.preventDefault();
				callback.call(dialog, dialog, this, cbId);
			});
		}

		return dialog;
	}

	/**
	 * @param {{
	 *  ok?: MpDialogButtonConf;
	 *  cancel?: MpDialogButtonConf;
	 *  okAction?: MpDialogCallback;
	 *  format?: string;
	 * } & MpModalOptions} options
	 */
	static confirm(options = {}) {

		const configConfirm = mpro.config.ui.dialog.confirm;
		const defaultButtons = configConfirm.default_buttons;
		const defaultOk = defaultButtons.ok;
		const defaultCancel = defaultButtons.cancel;
		const ok = options.ok || {};
		const cancel = options.cancel || {};
		let { okAction, title, titleIcon, content, format = configConfirm.default_format } = options;

		delete options.ok;
		delete options.cancel;
		delete options.okAction;

		// Use the default title
		if (title === undefined) {
			title = configConfirm.default_title;
		}

		// Use the default title icon
		if (titleIcon === undefined) {
			titleIcon = configConfirm.default_title_icon;
		}

		// Use the default content
		if (content === undefined) {
			content = configConfirm.default_content;
		}

		const okAttr = {};

		// Use the danger format
		switch (format) {
			case 'danger': {
				okAttr.class = 'btn btn-danger';
				titleIcon = {
					type: 'danger',
					format: 'danger',
					size: 'lg',
					right: '2',
				};

				break;
			}
			case 'dark': {
				okAttr.class = 'btn btn-dark';

				break;
			}
			case 'primary':
			case 'default': {
				okAttr.class = 'btn btn-primary';

				break;
			}
			// No default
		}

		const dialog = this.ask({
			...options,
			title,
			titleIcon,
			content,
			buttons: [
				{
					type: defaultOk.type,
					text: defaultOk.text || mpro.langvar('button_continue'),
					attributes: { ...defaultOk.attributes, ...okAttr },
					callback: okAction,
					...ok,
				},
				{
					type: defaultCancel.type,
					text: defaultCancel.text || mpro.langvar('button_cancel'),
					attributes: defaultCancel.attributes,
					...cancel,
				},
			],
		});
		return dialog;
	}

	/**
	 * @param {{
	 *  ok?: MpDialogButtonConf;
	 *  okAction?: MpDialogCallback;
	 * } & MpModalOptions} options
	 */
	static alert(options = {}) {

		const configAlert = mpro.config.ui.dialog.alert;
		const defaultButtons = configAlert.default_buttons;
		const defaultOk = defaultButtons.ok;
		const ok = options.ok || {};
		const { okAction, titleIcon } = options;

		delete options.ok;
		delete options.okAction;

		// Use the default title icon
		if (titleIcon === undefined) {
			options.titleIcon = configAlert.default_title_icon;
		}

		const dialog = this.ask({
			backdrop: true,
			...options,
			buttons: [
				{
					type: defaultOk.type, text: defaultOk.text || mpro.langvar('button_continue'), attributes: defaultOk.attributes, callback: okAction, ...ok,
				},
			],
		});
		return dialog;
	}

	// ==============
	// Public Methods
	// ==============
}
